import React, { useState } from 'react';
import { RefreshCw } from 'lucide-react';
import { Button } from 'components/ui/button';
import { Label } from 'components/ui/label';
import { Input } from 'components/ui/input';
import { Textarea } from 'components/ui/textarea';
import { Badge } from 'components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from 'components/ui/select';
import RunProgress from './RunProgress';
import { ACTIVE } from './util';

export const ModelSelect = ({ models, value, onChange, allowDefault = true }) => (
  <Select value={value || '__default'} onValueChange={(v) => onChange(v === '__default' ? null : v)}>
    <SelectTrigger className="h-9">
      <SelectValue placeholder="Model" />
    </SelectTrigger>
    <SelectContent>
      {allowDefault && <SelectItem value="__default">Template default</SelectItem>}
      {Object.entries(models || {}).map(([id, label]) => (
        <SelectItem key={id} value={id}>
          {label}
        </SelectItem>
      ))}
    </SelectContent>
  </Select>
);

export default function StageVideo({
  post, video, blogTypes, models, run, onStart, isAdmin,
}) {
  const [blogTypeId, setBlogTypeId] = useState(post?.blog_type_id ? String(post.blog_type_id) : '');
  const [targetGw, setTargetGw] = useState(post?.target_gw ?? '');
  const [model, setModel] = useState(null);
  const [presenter, setPresenter] = useState(post?.presenter_wp_id ?? '');
  const [pasteOpen, setPasteOpen] = useState(false);
  const [transcript, setTranscript] = useState('');
  const active = run && ACTIVE.has(run.status);
  const videoId = post?.source_video_id || video?.id;
  const title = post?.source_video_title || video?.title;

  const start = (extra = {}) =>
    onStart({
      blog_type_id: blogTypeId ? parseInt(blogTypeId, 10) : null,
      target_gw: targetGw === '' ? null : parseInt(targetGw, 10),
      model,
      presenter_wp_id: presenter === '' ? null : parseInt(presenter, 10),
      transcript_text: pasteOpen && transcript.trim() ? transcript : null,
      ...extra,
    });

  return (
    <div className="space-y-4">
      <div className="flex gap-4">
        {videoId && (
          <img src={`https://i.ytimg.com/vi/${videoId}/mqdefault.jpg`} alt="" className="h-[90px] w-[160px] rounded-md object-cover" />
        )}
        <div className="min-w-0 flex-1">
          <p className="truncate text-token-md font-medium">{title}</p>
          <a className="text-token-base text-primary underline" href={`https://www.youtube.com/watch?v=${videoId}`} target="_blank" rel="noreferrer">
            Open on YouTube
          </a>
          <div className="mt-2 flex flex-wrap gap-2">
            {post?.season_key && <Badge variant="secondary">Season {post.season}</Badge>}
            {post?.target_gw != null && <Badge variant="secondary">Target GW{post.target_gw}</Badge>}
            {post?.extraction?.transcript && (
              <Badge variant={post.extraction.transcript.truncated ? 'warning' : 'outline'}>
                Transcript: {post.extraction.transcript.source} · {Math.round(post.extraction.transcript.chars / 1000)}k chars
                {post.extraction.transcript.truncated ? ' · TRUNCATED' : ''}
              </Badge>
            )}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <div>
          <Label>Blog type</Label>
          <Select value={blogTypeId || '__none'} onValueChange={(v) => setBlogTypeId(v === '__none' ? '' : v)}>
            <SelectTrigger className="h-9"><SelectValue placeholder="Choose…" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="__none">General post</SelectItem>
              {(blogTypes || []).map((t) => (
                <SelectItem key={t.id} value={String(t.id)}>{t.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label>Target GW (blank = auto)</Label>
          <Input type="number" min={0} max={38} value={targetGw} onChange={(e) => setTargetGw(e.target.value)} className="h-9" />
        </div>
        <div>
          <Label>Model</Label>
          <ModelSelect models={models} value={model} onChange={setModel} />
        </div>
        {isAdmin && (
          <div>
            <Label>Presenter wp_id (author)</Label>
            <Input type="number" value={presenter} onChange={(e) => setPresenter(e.target.value)} placeholder="you" className="h-9" />
          </div>
        )}
      </div>

      {pasteOpen && (
        <div>
          <Label>Paste the transcript (plain text or .srt; [mm:ss] markers are kept)</Label>
          <Textarea rows={8} value={transcript} onChange={(e) => setTranscript(e.target.value)} />
        </div>
      )}

      <RunProgress run={run} label="Reading the video">
        <div className="flex flex-wrap gap-2">
          <Button size="sm" onClick={() => start()}>Retry</Button>
          <Button size="sm" variant="outline" onClick={() => start({ refresh_transcript: true })}>
            <RefreshCw /> Refresh captions
          </Button>
          <Button size="sm" variant="outline" onClick={() => setPasteOpen(true)}>Paste transcript instead</Button>
        </div>
      </RunProgress>

      {!active && (
        <div className="flex flex-wrap gap-2">
          <Button onClick={() => start()}>{post?.extraction ? 'Re-run extract' : 'Start'}</Button>
          {!pasteOpen && (
            <Button variant="ghost" size="sm" onClick={() => setPasteOpen(true)}>Paste a transcript instead</Button>
          )}
        </div>
      )}
    </div>
  );
}
